/**
 * Wizard — motor "uma pergunta por vez" das solicitações (mobile first).
 *
 * Recebe uma lista de `passos` (reconstruída pelo container a cada render) e
 * cuida de: navegação (cursor), barra de progresso, trava de obrigatórios
 * (Próxima só habilita quando `passo.valido()` passa) e edição pontual a
 * partir da tela de Revisão (volta para o passo, edita, retorna à revisão).
 *
 * O container é dono do ESTADO (cabeçalho, peças/itens, rascunho, envio);
 * este componente é apenas a casca de navegação — nenhuma regra de negócio.
 *
 * Contrato de cada passo:
 *   { key, grupo, kind: 'campo'|'boundary'|'revisao',
 *     titulo?, descricao?, obrigatorio?, opcional?,
 *     valido?: () => boolean,          // trava do Próxima (campo)
 *     render: (nav) => ReactNode }
 *
 * `nav` disponível dentro de render:
 *   { proximo, voltar, irParaChave(k), irParaRevisao, editar(k),
 *     jumpQuandoPronto(k) }  // jump p/ um passo que ainda vai existir (add peça)
 *
 * Dois campos OPCIONAIS do passo cuidam de tornar reversível o clique acidental
 * em "Adicionar outra peça/item" (o passo novo nasce inválido e, como fica
 * ANTES do boundary e da revisão, prendia o usuário sem saída):
 *   acaoDescartar?: { rotulo, executar(nav) }  // link discreto sob a pergunta
 *   aoVoltar?: (nav) => boolean                // assume o "Voltar"; true = tratou
 */
import React, { useEffect, useRef, useState } from "react";
import { FiArrowLeft, FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { PageContainer } from "../../../styles/shared";
import {
  Asterisco, BtnPrimario, BtnSecundario, PassoContador, PassoLinha,
  ProgressoFill, ProgressoTrack, StepArea, StepCorpo, StepDescartar,
  StepDescricao, StepGrupoChip, StepPergunta, TagOpcional, WizardCol,
  WizardRodape, WizardTopo, WizardTopoTitulo, WizardVoltarIcone,
} from "./styles";

// Campos "digitáveis" que encadeiam por Enter dentro de um passo. Botões
// (toggle/segmento/stepper), <select> e <textarea> mantêm a semântica nativa do
// Enter (ativar, abrir opção, quebra de linha) — não são origem de interceptação,
// mas entram como ALVOS de foco na sequência.
const SELETOR_FOCAVEIS =
  'input:not([type="hidden"]):not([type="file"]):not([disabled]),' +
  'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function camposFocaveis(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(SELETOR_FOCAVEIS))
    .filter((el) => el.offsetParent !== null); // ignora escondidos (display:none)
}

function ehInputTexto(el) {
  if (!el || el.tagName !== "INPUT") return false;
  const t = (el.getAttribute("type") || "text").toLowerCase();
  return !["checkbox", "radio", "button", "submit", "reset", "file", "range"].includes(t);
}

export default function Wizard({ titulo, codigo, aoSair, passos, onEnviar, aviso, podeEnviar = true }) {
  const [cursor, setCursor] = useState(0);
  const [retorno, setRetorno] = useState(false); // veio da revisão para editar
  const [pendente, setPendente] = useState(null); // key a saltar quando existir
  const colRef = useRef(null);

  const total = passos.length;
  // O salto pendente é resolvido AQUI, no render, e não só no efeito abaixo.
  // Se ficasse só no efeito, remover um bloco pintaria um frame no passo
  // errado (a lista encolheu e o cursor antigo agora aponta para outra coisa)
  // e o efeito de clamp — que roda depois e lê o `cursor` velho do closure —
  // ainda sobrescreveria o destino, jogando o usuário na revisão.
  const alvoPendente = pendente
    ? passos.findIndex((p) => p.key === pendente)
    : -1;
  const idx = alvoPendente >= 0
    ? alvoPendente
    : Math.min(Math.max(cursor, 0), Math.max(0, total - 1));
  const step = passos[idx];

  // Volta ao topo a cada troca de passo. Sem isto, uma pergunta alta deixa o
  // scroll no meio e a próxima pergunta nasce escondida no topo da tela.
  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo(0, 0);
    // Rede de segurança: se quem rolar for um ancestral com overflow próprio
    // (e não a janela), zera o scroll dele também.
    let el = colRef.current?.parentElement;
    while (el && el !== document.body) {
      if (el.scrollTop) el.scrollTop = 0;
      el = el.parentElement;
    }
  }, [idx]);

  // Salta para um passo que só passa a existir depois de mutar o estado
  // (ex.: "Adicionar peça" cria a peça e só então o passo dela existe).
  // O destino já vale desde o render (ver `alvoPendente`); aqui só firmamos
  // o cursor e apagamos a pendência.
  useEffect(() => {
    if (alvoPendente < 0) return;
    setCursor(alvoPendente);
    setPendente(null);
  }, [alvoPendente]);

  // Mantém o cursor válido se a lista encolher (remoção de peça/item).
  // Cede a vez enquanto há salto pendente: senão este efeito, que lê o `cursor`
  // velho, venceria o salto (ambos caem no mesmo lote) e o destino escolhido
  // seria descartado.
  useEffect(() => {
    if (pendente) return;
    if (cursor > total - 1) setCursor(Math.max(0, total - 1));
  }, [total, cursor, pendente]);

  const irParaChave = (chave) => {
    const i = passos.findIndex((p) => p.key === chave);
    if (i >= 0) setCursor(i);
  };
  const irParaRevisao = () => irParaChave("revisao");

  const proximo = () => {
    if (retorno) {
      setRetorno(false);
      const i = passos.findIndex((p) => p.kind === "revisao");
      setCursor(i >= 0 ? i : Math.min(idx + 1, total - 1));
      return;
    }
    setCursor((c) => Math.min(c + 1, total - 1));
  };

  const voltar = () => {
    if (retorno) {
      // No modo de edição não deixa escapar para a revisão com um obrigatório
      // inválido — mesma trava que já vale para o "Próxima".
      if (typeof step.valido === "function" && !step.valido()) return;
      setRetorno(false);
      irParaRevisao();
      return;
    }
    // O passo pode assumir o "Voltar" — é o que descarta a peça/item recém
    // adicionado e ainda em branco, para o clique acidental em "Adicionar"
    // não virar uma armadilha sem saída.
    if (typeof step.aoVoltar === "function" && step.aoVoltar(nav)) return;
    if (idx <= 0) {
      aoSair?.();
      return;
    }
    setCursor((c) => Math.max(c - 1, 0));
  };

  const editar = (chave) => {
    setRetorno(true);
    irParaChave(chave);
  };

  const nav = {
    proximo,
    voltar,
    irParaChave,
    irParaRevisao,
    editar,
    // Saltar por chave é sempre REENTRADA de fluxo (adicionou/duplicou/
    // descartou um bloco), nunca continuação de uma edição pontual — então
    // encerra o modo de edição junto. Sem isto, descartar um bloco aberto pela
    // revisão deixava `retorno` pendurado: o "Voltar" passava a ir para
    // frente, e a primeira "Próxima" do bloco seguinte pulava direto para a
    // revisão com a peça pela metade e o "Enviar" desabilitado sem explicação.
    jumpQuandoPronto: (chave) => {
      setRetorno(false);
      setPendente(chave);
    },
  };

  if (!step) return null;

  // Progresso conta só os passos "campo" (as perguntas de verdade).
  const totalCampos = passos.filter((p) => p.kind === "campo").length;
  const feitos = passos.slice(0, idx + 1).filter((p) => p.kind === "campo").length;
  const emCampo = step.kind === "campo";
  const pct = emCampo && totalCampos ? (feitos / totalCampos) * 100 : 100;

  const bloqueado = typeof step.valido === "function" && !step.valido();
  // No passo inicial "Voltar" na verdade SAI do formulário (rótulo distinto).
  const noInicio = idx <= 0 && !retorno;
  // Em modo de edição com obrigatório inválido, travar também o "Voltar".
  const voltarTravado = retorno && bloqueado;
  const labelId = `${step.key}-label`;
  const voltarAria = noInicio
    ? "Sair do formulário"
    : retorno
      ? "Voltar à revisão"
      : "Passo anterior";

  // Enter (desktop): vai para o próximo campo da MESMA pergunta; sendo o último
  // (ou fora da lista de campos), dispara "Próxima" quando o passo é válido — o
  // mesmo gate do botão. Só intercepta a partir de um <input> de texto; botão,
  // <select> e <textarea> seguem o comportamento nativo. Tab fica nativo.
  const aoTeclarNoPasso = (e) => {
    if (e.key !== "Enter" || e.nativeEvent?.isComposing) return;
    if (!ehInputTexto(e.target)) return;
    const focaveis = camposFocaveis(e.currentTarget);
    const i = focaveis.indexOf(e.target);
    e.preventDefault(); // evita submit implícito
    if (i >= 0 && i < focaveis.length - 1) {
      focaveis[i + 1].focus();
      return;
    }
    if (step.kind === "campo" && !bloqueado) proximo();
  };

  return (
    <PageContainer>
      <WizardCol ref={colRef}>
        <WizardTopo>
          <WizardVoltarIcone
            type="button"
            aria-label={voltarAria}
            disabled={voltarTravado}
            onClick={voltar}
          >
            <FiArrowLeft size={20} />
          </WizardVoltarIcone>
          <WizardTopoTitulo>
            <strong>{titulo}</strong>
            <span>{codigo}</span>
          </WizardTopoTitulo>
        </WizardTopo>

        <ProgressoTrack aria-hidden="true">
          <ProgressoFill style={{ width: `${pct}%` }} />
        </ProgressoTrack>

        <PassoLinha>
          <StepGrupoChip>{step.grupo}</StepGrupoChip>
          <PassoContador>
            {emCampo
              ? `Pergunta ${feitos} de ${totalCampos}`
              : step.kind === "revisao"
                ? "Revisão"
                : "Quase lá"}
          </PassoContador>
        </PassoLinha>

        {aviso}

        <StepArea
          key={step.key}
          role="group"
          aria-labelledby={step.titulo ? labelId : undefined}
          onKeyDown={aoTeclarNoPasso}
        >
          {step.titulo && (
            <StepPergunta id={labelId}>
              <span>
                {step.titulo}
                {step.obrigatorio && <Asterisco aria-label="obrigatório"> *</Asterisco>}
              </span>
              {step.opcional && <TagOpcional>opcional</TagOpcional>}
            </StepPergunta>
          )}
          {step.descricao && <StepDescricao>{step.descricao}</StepDescricao>}
          <StepCorpo>{step.render(nav)}</StepCorpo>
          {step.acaoDescartar && (
            <StepDescartar
              type="button"
              onClick={() => step.acaoDescartar.executar(nav)}
            >
              {step.acaoDescartar.rotulo}
            </StepDescartar>
          )}
        </StepArea>

        <WizardRodape>
          <BtnSecundario type="button" onClick={voltar} disabled={voltarTravado}>
            {noInicio ? (
              <>
                <FiX size={18} /> Sair
              </>
            ) : (
              <>
                <FiChevronLeft size={18} /> Voltar
              </>
            )}
          </BtnSecundario>

          {step.kind === "campo" && (
            <BtnPrimario type="button" onClick={proximo} disabled={bloqueado}>
              Próxima <FiChevronRight size={18} />
            </BtnPrimario>
          )}

          {step.kind === "revisao" && (
            <AsyncButton
              onClick={onEnviar}
              disabled={!podeEnviar}
              silentError
              style={{ flex: 1, minHeight: 52, fontSize: 16, justifyContent: "center" }}
            >
              Enviar solicitação
            </AsyncButton>
          )}
        </WizardRodape>
      </WizardCol>
    </PageContainer>
  );
}
